<template>
  <div class="quiz-container">
    <div class="bank-gate" v-if="needBankPick">
      <div class="bank-gate-header">
        <div class="header-content">
          <div class="header-center">选择题库</div>
        </div>
      </div>
      <div class="bank-gate-body">
        <BankPicker
          :banks="banks"
          :loading="banksLoading"
          subtitle="开通会员后可答对应题库；已开通高等级不可再购低等级"
          @select="onBankSelect"
        />
      </div>
    </div>

    <template v-else>
    <div class="bg-decoration">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>
    
    <div class="header" v-if="quizStarted">
      <div class="header-content">
        <div class="header-left" @click="goBack">
          <div class="back-btn">
            <van-icon name="arrow-left" size="20" />
          </div>
        </div>
        <div class="header-center">
          <div class="quiz-title">🎵 音乐答题</div>
          <div class="quiz-progress-text">{{ currentQuestionIndex + 1 }}/{{ currentQuestionsCount }}</div>
        </div>
        <div class="header-right">
          <div class="timer-badge">
            <van-icon name="clock-o" size="18" />
            <span class="timer-text">{{ formatTotalTime(totalTime) }}</span>
          </div>
        </div>
      </div>
      
      <div class="progress-bar-wrapper">
        <div class="progress-bar" :style="{ width: progress + '%' }"></div>
        <div class="progress-glow" :style="{ left: progress + '%' }"></div>
      </div>
    </div>

    <div class="content empty-quiz" v-if="quizStarted && !currentQuestion">
      <van-empty description="当前分类暂无题目">
        <van-button round type="primary" @click="goBack">返回首页</van-button>
      </van-empty>
    </div>

    <div class="content" v-if="currentQuestion && quizStarted">
      <div class="question-card animate-card-pop">
        <div class="card-glow"></div>
        <div class="question-header">
          <div class="question-type-tag" :class="'type-' + currentQuestion.type">
            <van-icon :name="questionTypeIcon" size="14" />
            <span>{{ questionTypeText }}</span>
          </div>
          <div class="question-header-right">
            <div class="question-number">
              <span class="number-bg">{{ currentQuestionIndex + 1 }}</span>
              <span class="number-text">/{{ currentQuestionsCount }}</span>
            </div>
          </div>
        </div>

        <!-- 朗读功能暂时隐藏 -->
        <div class="read-button-wrapper" v-if="false">
          <button
            type="button"
            class="read-title-btn"
            :class="{ active: isSpeakingTitle }"
            @click.stop="toggleReadQuestionTitle"
          >
            <span class="read-title-icon">{{ isSpeakingTitle ? '🔊' : '🔈' }}</span>
            <span>{{ isSpeakingTitle ? '停止' : '朗读题目' }}</span>
          </button>
        </div>

        <h2 class="question-title animate-fade-in-up">{{ currentQuestion.title }}</h2>
        
        <div class="audio-section audio-section-stem" v-if="hasStemAudio">
          <div class="audio-card">
            <div class="audio-visualizer" :class="{ 'playing': isPlaying }">
              <div v-for="i in 8" :key="i" class="bar" :style="{ animationDelay: i * 0.1 + 's' }"></div>
            </div>
            <div class="audio-controls">
              <van-button 
                round 
                :type="isPlaying ? 'default' : 'primary'"
                :icon="isPlaying ? 'pause-circle-o' : 'play-circle-o'"
                @click="toggleAudio"
                class="audio-btn"
              >
                {{ isPlaying ? '暂停' : '播放' }}
              </van-button>
              <span class="audio-time">{{ formatTime(currentTime) }} / {{ formatTime(questionAudioDisplayDuration) }}</span>
            </div>
          </div>
          <audio
            ref="audioPlayer"
            :key="'q-' + currentQuestion.id + '-' + questionAudioSrc"
            :src="questionAudioSrc"
            preload="metadata"
            class="hidden-audio"
            @loadedmetadata="onQuestionAudioLoadedMetadata"
            @ended="onAudioEnded"
            @pause="isPlaying = false"
            @play="isPlaying = true"
          />
        </div>

        <div class="audio-section audio-section-record" v-if="currentQuestion.type === 3">
          <div class="record-card" :class="{ 'recording': isRecording }">
            <div class="record-indicator" v-if="isRecording">
              <span class="record-dot"></span>
              <span class="record-dot"></span>
              <span class="record-dot"></span>
            </div>
            <van-button 
              round 
              :type="isRecording ? 'danger' : 'info'"
              :icon="isRecording ? 'stop-circle-o' : 'audio'"
              @click="toggleRecord"
              class="record-btn"
            >
              {{ isRecording ? '停止录音' : '开始录音' }}
            </van-button>
            <p class="record-time" v-if="isRecording">{{ recordTime }}s</p>
          </div>
          
          <div class="play-recorded-card" v-if="recordedAudio">
            <div class="recorded-audio-visualizer" :class="{ 'playing': isRecordedAudioPlaying }">
              <div v-for="i in 6" :key="i" class="bar" :style="{ animationDelay: i * 0.15 + 's' }"></div>
            </div>
            <div class="recorded-audio-controls">
              <van-button 
                round 
                type="success" 
                :icon="isRecordedAudioPlaying ? 'pause-circle-o' : 'play-circle-o'"
                @click="toggleRecordedAudio"
              >
                {{ isRecordedAudioPlaying ? '暂停' : '播放录音' }}
              </van-button>
              <span class="audio-time">{{ formatTime(recordedAudioTime) }} / {{ formatTime(recordedAudioDisplayDuration) }}</span>
            </div>
          </div>
          <audio
            v-if="recordedAudioUrl"
            ref="recordedAudioPlayer"
            :key="'rec-' + recordedAudioUrl"
            :src="recordedAudioUrl"
            preload="metadata"
            class="hidden-audio"
            @loadedmetadata="onRecordedAudioLoadedMetadata"
            @ended="onRecordedAudioEnded"
            @pause="isRecordedAudioPlaying = false"
            @play="isRecordedAudioPlaying = true"
          />
        </div>

        <div class="options" v-if="currentQuestion.type === 1 || currentQuestion.type === 2">
          <div 
            v-for="(option, index) in currentQuestion.options" 
            :key="option.option_key"
            class="option-item"
            :class="{ 
              'selected': selectedOptions.includes(option.option_key),
              'correct': showResult && correctOptions.includes(option.option_key),
              'wrong': showResult && selectedOptions.includes(option.option_key) && !correctOptions.includes(option.option_key)
            }"
            :style="{ animationDelay: index * 0.1 + 's' }"
            @click="selectOption(option.option_key)"
          >
            <div class="option-key">
              <span class="key-inner">{{ option.option_key }}</span>
              <van-icon name="check" size="16" v-if="selectedOptions.includes(option.option_key)" />
              <van-icon name="check" size="16" v-if="showResult && correctOptions.includes(option.option_key)" />
            </div>
            <div class="option-text">{{ option.option_text }}</div>
            <div class="option-indicator" v-if="showResult && correctOptions.includes(option.option_key)">✓</div>
            <div class="option-indicator wrong" v-if="showResult && selectedOptions.includes(option.option_key) && !correctOptions.includes(option.option_key)">✗</div>
          </div>
        </div>
      </div>

    </div>

    <div class="result-modal" v-if="showResult" @click="closeModal">
      <div class="modal-mask" @click="closeModal"></div>
      <div class="modal-content animate-bounce-in" :class="{ 'correct': isCorrect, 'wrong': !isCorrect }" @click.stop>
        <div class="modal-glow"></div>
        <div class="modal-icon">
          <div class="icon-ring"></div>
          <van-icon :name="isCorrect ? 'checked-circle' : 'close-circle'" size="48" />
        </div>
        <h3>{{ isCorrect ? '🎉 回答正确' : '😅 回答错误' }}</h3>
        <div class="modal-tip">{{ isCorrect ? '太棒了！继续保持' : '再接再厉，下次一定行' }}</div>
        <div class="score-display">
          <span class="score-label">本次得分</span>
          <span class="score-value">+{{ currentScore }}</span>
        </div>
        <van-button 
          type="primary" 
          block 
          round
          size="large"
          class="modal-next-btn"
          @click="goNext"
        >
          {{ currentQuestionIndex + 1 >= currentQuestionsCount ? '查看结果' : '下一题' }}
          <van-icon name="arrow-right" size="18" />
        </van-button>
      </div>
    </div>

    <div class="footer" v-if="quizStarted">
      <div class="footer-safe-area"></div>
      <div class="footer-content">
        <van-button 
          v-if="!showResult && currentQuestion && currentQuestion.type !== 3"
          type="primary" 
          block 
          round
          size="large"
          :disabled="selectedOptions.length === 0"
          class="submit-btn"
          @click="submitAnswer"
        >
          <span>提交答案</span>
          <van-icon name="arrow-right" size="18" />
        </van-button>
        <van-button 
          v-else-if="currentQuestion && currentQuestion.type === 3 && recordedAudio"
          type="primary" 
          block 
          round
          size="large"
          class="upload-btn"
          @click="uploadAudio"
        >
          <span>上传录音</span>
          <van-icon name="upload" size="18" />
        </van-button>
        <div v-else-if="currentQuestion && currentQuestion.type === 3 && !recordedAudio" class="record-hint">
          <van-icon name="info-o" size="14" />
          <span>请先录音后再上传</span>
        </div>
      </div>
    </div>

    <van-dialog v-model="showVipDialog" title="升级VIP" show-cancel-button class="vip-dialog">
      <div class="vip-dialog-content">
        <div class="vip-icon">👑</div>
        <p>免费用户只能回答前5题</p>
        <p>升级VIP解锁更多题目！</p>
      </div>
      <van-button type="primary" block class="vip-btn" @click="goToVip">立即升级</van-button>
    </van-dialog>

    <van-dialog
      v-model="showStartDialog"
      title=""
      show-cancel-button
      confirm-button-text="开始答题"
      cancel-button-text="取消"
      @confirm="confirmStartQuiz"
      @cancel="cancelStartQuiz"
      class="start-dialog"
    >
      <div class="start-dialog-content">
        <div class="start-icon">🎵</div>
        <h3>准备好挑战了吗？</h3>
        <div class="start-info">
          <div class="info-item">
            <van-icon name="file-text" size="18" />
            <span>「{{ currentSubCategoryName || currentCategoryName || '当前训练' }}」共 {{ currentQuestionsCount }} 道题目</span>
          </div>
          <div class="info-item">
            <van-icon name="clock-o" size="18" />
            <span>预计 30 分钟</span>
          </div>
        </div>
        <p class="start-tip">答题开始后将计时，请确保有足够的时间完成答题</p>
      </div>
    </van-dialog>
    </template>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { mapGetters } from 'vuex'
import { resolveMediaUrl } from '@/utils/media'
import { speakQuestionTitle, stopSpeech } from '@/utils/speech'
import BankPicker from '@/components/BankPicker.vue'

export default {
  name: 'Quiz',
  components: { BankPicker },
  data() {
    return {
      selectedOptions: [],
      showResult: false,
      isCorrect: false,
      currentScore: 0,
      correctOptions: [],
      isPlaying: false,
      currentTime: 0,
      isRecording: false,
      recordTime: 0,
      recordTimer: null,
      recordedAudio: null,
      recordedAudioUrl: null,
      isRecordedAudioPlaying: false,
      recordedAudioTime: 0,
      mediaRecorder: null,
      audioChunks: [],
      showVipDialog: false,
      showStartDialog: false,
      nextData: null,
      totalTime: 0,
      timer: null,
      quizStarted: false,
      needBankPick: false,
      banks: [],
      banksLoading: false,
      currentBankName: '',
      questionAudioMetaDuration: 0,
      recordedAudioMetaDuration: 0,
      isSpeakingTitle: false
    }
  },
  computed: {
    ...mapGetters(['currentQuestion', 'progress', 'isVip', 'currentCategoryName', 'currentSubCategoryName']),
    questions() {
      return this.$store.state.questions
    },
    currentQuestionIndex() {
      return this.$store.state.currentQuestionIndex
    },
    currentSubCategory() {
      return this.$store.state.currentSubCategory
    },
    currentQuestionsCount() {
      return this.$store.state.questions.length || 0
    },
    isLastQuestion() {
      return this.currentQuestionIndex >= this.currentQuestionsCount - 1
    },
    questionTypeText() {
      const types = {
        1: '单选题',
        2: '多选题',
        3: '音乐录音题'
      }
      return types[this.currentQuestion?.type] || ''
    },
    questionTypeIcon() {
      const icons = {
        1: 'circle-o',
        2: 'check-square-o',
        3: 'music-o'
      }
      return icons[this.currentQuestion?.type] || 'help-o'
    },
    hasStemAudio() {
      const q = this.currentQuestion
      if (!q) return false
      return !!this.getAudioUrl(q.audio_url)
    },
    questionAudioSrc() {
      const q = this.currentQuestion
      if (!q || !this.hasStemAudio) return ''
      return this.getAudioUrl(q.audio_url)
    },
    questionAudioDisplayDuration() {
      const q = this.currentQuestion
      const fromDb = q && Number(q.audio_duration)
      if (fromDb && fromDb > 0) return fromDb
      if (this.questionAudioMetaDuration > 0) return this.questionAudioMetaDuration
      return 0
    },
    recordedAudioDisplayDuration() {
      if (this.recordedAudioMetaDuration > 0) return this.recordedAudioMetaDuration
      return this.recordedAudioTime
    }
  },
  watch: {
    quizStarted(val) {
      this.$store.commit('SET_QUIZ_HIDE_TABBAR', !!val)
    },
    needBankPick(val) {
      if (val) {
        this.$store.commit('SET_QUIZ_HIDE_TABBAR', false)
      }
    },
    '$route'(to) {
      if (to.path !== '/quiz') return
      const bankId = parseInt(to.query.bank_id, 10) || 0
      const subCategoryId = parseInt(to.query.sub_category_id, 10) || 0
      if (bankId > 0 && subCategoryId > 0) {
        this.currentBankName = to.query.bank_name || this.currentBankName
        this.needBankPick = false
        this.quizStarted = false
        this.showStartDialog = false
        this.initQuiz(bankId)
      }
    }
  },
  async mounted() {
    this.$store.commit('SET_QUIZ_HIDE_TABBAR', false)
    const bankId = parseInt(this.$route.query.bank_id, 10) || 0
    this.currentBankName = this.$route.query.bank_name || ''
    if (bankId > 0) {
      await this.initQuiz(bankId)
    } else {
      await this.loadBanksForPick()
    }
  },
  beforeDestroy() {
    this.$store.commit('SET_QUIZ_HIDE_TABBAR', false)
    stopSpeech()
    this.stopTimer()
    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl)
    }
    if (this.recordTimer) {
      clearInterval(this.recordTimer)
    }
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop()
    }
  },
  methods: {
    async loadBanksForPick() {
      this.banksLoading = true
      this.needBankPick = true
      try {
        if (this.$store.state.token) {
          await this.$store.dispatch('fetchUserInfo')
        }
        const data = await this.$store.dispatch('fetchQuestionBanks')
        this.banks = data.banks || []
      } catch (err) {
        Toast.fail(err.message || '加载题库失败')
      } finally {
        this.banksLoading = false
      }
    },
    async onBankSelect(bank) {
      if (!bank) return
      localStorage.setItem('last_bank_id', String(bank.id))
      if (!bank.has_access) {
        if (bank.purchase_blocked) {
          Toast('您已开通更高等级题库，无需购买本题库')
          return
        }
        this.$router.push({ path: '/vip', query: { bank_id: bank.id, bank_name: bank.name } })
        return
      }
      this.$router.push({
        path: '/training',
        query: { bank_id: bank.id, bank_name: bank.name }
      })
    },
    async initQuiz(bankId) {
      const subCategoryId = parseInt(this.$route.query.sub_category_id, 10) || 0
      const categoryId = parseInt(this.$route.query.category_id, 10) || 0
      if (bankId > 0 && subCategoryId <= 0) {
        this.$router.replace({
          path: '/training',
          query: { bank_id: bankId, bank_name: this.currentBankName || this.$route.query.bank_name || '' }
        })
        return
      }
      try {
        await this.$store.dispatch('startSubCategoryQuiz', {
          bankId,
          categoryId,
          subCategoryId,
          subCategoryName: this.$route.query.sub_category_name || '',
          categoryName: this.$route.query.category_name || ''
        })
        this.showStartDialog = true
      } catch (err) {
        Toast.fail(err.message || '加载题目失败')
        if (parseInt(this.$route.query.bank_id, 10) > 0) {
          this.$router.back()
        } else {
          this.needBankPick = true
        }
      }
    },
    onQuestionAudioLoadedMetadata(e) {
      const d = e && e.target && e.target.duration
      if (!d || !isFinite(d) || d <= 0) return
      this.questionAudioMetaDuration = Math.floor(d)
    },
    onRecordedAudioLoadedMetadata(e) {
      const d = e && e.target && e.target.duration
      if (!d || !isFinite(d) || d <= 0) return
      this.recordedAudioMetaDuration = Math.floor(d)
    },
    async confirmStartQuiz() {
      if (!this.$store.state.questions.length) {
        Toast.fail('暂无题目')
        this.$router.back()
        return
      }
      this.checkVipAccess()
      const catId = this.$store.state.currentCategory?.id
      if (catId) {
        localStorage.removeItem('quiz_timer_cat_' + catId)
      }
      this.startTimer()
      this.quizStarted = true
    },
    cancelStartQuiz() {
      this.$router.back()
    },
    checkVipAccess() {
      if (this.currentQuestionIndex >= 5 && !this.isVip) {
        this.showVipDialog = true
      }
    },
    trainingBackRoute() {
      const bankId = parseInt(this.$route.query.bank_id, 10) || 0
      if (bankId > 0) {
        return {
          path: '/training',
          query: {
            bank_id: bankId,
            bank_name: this.$route.query.bank_name || this.currentBankName || ''
          }
        }
      }
      return this.$store.getters.smartBackRoute
    },
    goBack() {
      const navigateBack = () => {
        const target = this.trainingBackRoute()
        if (typeof target === 'string') {
          this.$router.push(target)
        } else {
          this.$router.push(target)
        }
      }
      if (this.quizStarted) {
        this.$dialog.confirm({
          title: '提示',
          message: '答题正在进行中，确定要退出吗？退出后答题进度将不会保存。'
        }).then(() => {
          this.stopTimer()
          navigateBack()
        }).catch(() => {
        })
      } else {
        navigateBack()
      }
    },
    selectOption(optionId) {
      if (this.showResult) return
      
      if (this.currentQuestion.type === 1) {
        this.selectedOptions = [optionId]
      } else {
        const index = this.selectedOptions.indexOf(optionId)
        if (index > -1) {
          this.selectedOptions.splice(index, 1)
        } else {
          this.selectedOptions.push(optionId)
        }
      }
    },
    async submitAnswer() {
      if (this.selectedOptions.length === 0) {
        Toast('请选择答案')
        return
      }

      try {
        const answer = this.selectedOptions.join(',')
        const result = await this.$store.dispatch('submitAnswer', {
          questionId: this.currentQuestion.id,
          answer
        })
        
        this.isCorrect = result.is_correct
        this.currentScore = result.score
        this.correctOptions = result.correct_options || []
        this.showResult = true
        
        this.nextData = result.next
        
        if (!this.isVip && result.answer_count >= 5) {
          Toast.fail('免费用户只能答题5次，请充值VIP')
          setTimeout(() => {
            this.$router.push('/vip')
          }, 2000)
        }
      } catch (err) {
        Toast.fail(err.message)
      }
    },
    closeModal() {
      this.showResult = false
    },
    goNext() {
      this.showResult = false
      this.nextQuestion()
    },
    async nextQuestion() {
      if (!this.nextData) {
        if (this.isLastQuestion) {
          await this.submitCategoryAnswers()
        } else {
          this.$store.commit('SET_CURRENT_QUESTION_INDEX', this.currentQuestionIndex + 1)
          this.resetQuestion()
          this.checkVipAccess()
        }
        return
      }
      
      this.resetQuestion()
      
      if (this.nextData.type === 'question') {
        const questionIndex = this.questions.findIndex(q => q.id === this.nextData.question_id)
        if (questionIndex >= 0) {
          this.$store.commit('SET_CURRENT_QUESTION_INDEX', questionIndex)
          this.checkVipAccess()
        }
      } else if (this.nextData.type === 'sub_category' || this.nextData.type === 'completed') {
        await this.submitCategoryAnswers()
      }
    },
    async submitCategoryAnswers() {
      Toast.loading('正在提交答题数据...')
      
      try {
        const answers = this.$store.state.answers || []
        const questions = this.$store.state.questions || []
        const bySub = {}

        answers.forEach((answer) => {
          let subId = answer.subCategoryId
          if (!subId) {
            const q = questions.find(item => item.id === answer.questionId)
            subId = q ? q.sub_category_id : this.currentSubCategory?.id
          }
          if (!subId) return
          if (!bySub[subId]) bySub[subId] = []
          bySub[subId].push({
            question_id: answer.questionId,
            answer: answer.answer
          })
        })

        const subIds = Object.keys(bySub)
        if (subIds.length === 0) {
          throw new Error('没有可提交的答案')
        }

        const timeEach = Math.max(1, Math.floor(this.totalTime / subIds.length))
        for (let i = 0; i < subIds.length; i++) {
          await this.$http.post('/api/answer/category/submit', {
            sub_category_id: parseInt(subIds[i], 10),
            total_time: i === subIds.length - 1 ? this.totalTime : timeEach,
            answers: bySub[subIds[i]]
          })
        }
        
        Toast.clear()
        Toast.success('答题提交成功！')
        
        try {
          await this.$store.dispatch('fetchUserInfo')
        } catch (e) {
          /* ignore */
        }
        
        this.stopTimer()
        
        const catId = this.$store.state.currentCategory?.id
        if (catId) {
          localStorage.removeItem('quiz_timer_cat_' + catId)
        }
        
        setTimeout(() => {
          this.$router.push('/records').catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              throw err
            }
          })
        }, 1500)
      } catch (err) {
        Toast.clear()
        Toast.fail(err.message)
      }
    },
    stopReadQuestionTitle() {
      stopSpeech()
      this.isSpeakingTitle = false
    },
    async toggleReadQuestionTitle() {
      if (this.isSpeakingTitle) {
        this.stopReadQuestionTitle()
        return
      }
      const title = this.currentQuestion && this.currentQuestion.title
      if (!title) return
      this.pauseQuestionAudio()
      this.pauseRecordedAudio()
      this.isSpeakingTitle = true
      try {
        await speakQuestionTitle({
          text: title,
          questionId: this.currentQuestion && this.currentQuestion.id
        })
      } catch (err) {
        Toast.fail(err.message || '朗读失败')
      } finally {
        this.isSpeakingTitle = false
      }
    },
    resetQuestion() {
      this.stopReadQuestionTitle()
      this.pauseQuestionAudio()
      this.pauseRecordedAudio()
      this.questionAudioMetaDuration = 0
      this.recordedAudioMetaDuration = 0
      this.selectedOptions = []
      this.showResult = false
      this.isCorrect = false
      this.currentScore = 0
      this.correctOptions = []
      this.recordedAudio = null
      if (this.recordedAudioUrl) {
        URL.revokeObjectURL(this.recordedAudioUrl)
      }
      this.recordedAudioUrl = null
      this.isRecordedAudioPlaying = false
      this.recordedAudioTime = 0
    },
    pauseQuestionAudio() {
      const audio = this.$refs.audioPlayer
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
      this.isPlaying = false
      this.currentTime = 0
    },
    pauseRecordedAudio() {
      const audio = this.$refs.recordedAudioPlayer
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
      this.isRecordedAudioPlaying = false
      this.recordedAudioTime = 0
    },
    async toggleAudio() {
      if (!this.questionAudioSrc) {
        Toast('本题暂无示范音频')
        return
      }
      const audio = this.$refs.audioPlayer
      if (!audio) {
        Toast('音频加载中，请稍后再试')
        return
      }
      if (this.isPlaying) {
        audio.pause()
        return
      }
      try {
        await audio.play()
      } catch (err) {
        console.error(err)
        Toast.fail('无法播放音频，请检查题目音频地址')
      }
    },
    async toggleRecordedAudio() {
      if (!this.recordedAudioUrl) {
        Toast('请先录音')
        return
      }
      const audio = this.$refs.recordedAudioPlayer
      if (!audio) {
        Toast('录音加载中，请稍后再试')
        return
      }
      if (this.isRecordedAudioPlaying) {
        audio.pause()
        return
      }
      try {
        await audio.play()
      } catch (err) {
        console.error(err)
        Toast.fail('无法播放录音')
      }
    },
    onTimeUpdate(e) {
      this.currentTime = Math.floor(e.target.currentTime)
    },
    onAudioEnded() {
      this.isPlaying = false
      this.currentTime = 0
    },
    onRecordedAudioTimeUpdate(e) {
      this.recordedAudioTime = Math.floor(e.target.currentTime)
    },
    onRecordedAudioEnded() {
      this.isRecordedAudioPlaying = false
      this.recordedAudioTime = 0
    },
    formatTime(seconds) {
      const min = Math.floor(seconds / 60)
      const sec = seconds % 60
      return `${min}:${sec.toString().padStart(2, '0')}`
    },
    getAudioUrl(url) {
      return resolveMediaUrl(url)
    },
    async toggleRecord() {
      if (this.isRecording) {
        this.stopRecord()
      } else {
        this.startRecord()
      }
    },
    async startRecord() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.mediaRecorder = new MediaRecorder(stream)
        this.audioChunks = []
        
        this.mediaRecorder.ondataavailable = (e) => {
          this.audioChunks.push(e.data)
        }
        
        this.mediaRecorder.onstop = () => {
          this.recordedAudioMetaDuration = 0
          const mimeType = (this.mediaRecorder && this.mediaRecorder.mimeType) || 'audio/webm'
          const audioBlob = new Blob(this.audioChunks, { type: mimeType })
          this.recordedAudio = audioBlob
          if (this.recordedAudioUrl) {
            URL.revokeObjectURL(this.recordedAudioUrl)
          }
          this.recordedAudioUrl = URL.createObjectURL(audioBlob)
        }
        
        this.mediaRecorder.start()
        this.isRecording = true
        this.recordTime = 0
        
        this.recordTimer = setInterval(() => {
          this.recordTime++
        }, 1000)
        
        Toast('开始录音')
      } catch (err) {
        Toast.fail('无法访问麦克风')
      }
    },
    stopRecord() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop()
        this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
        this.isRecording = false
        clearInterval(this.recordTimer)
        Toast('录音完成')
      }
    },
    async uploadAudio() {
      if (!this.recordedAudio) {
        Toast('请先录音')
        return
      }
      
      Toast.loading('上传中...')
      
      try {
        const formData = new FormData()
        const mime = (this.recordedAudio && this.recordedAudio.type) ? this.recordedAudio.type : 'audio/webm'
        let ext = 'webm'
        if (mime.indexOf('mp4') !== -1 || mime.indexOf('m4a') !== -1) {
          ext = 'm4a'
        } else if (mime.indexOf('ogg') !== -1) {
          ext = 'ogg'
        } else if (mime.indexOf('wav') !== -1) {
          ext = 'wav'
        } else if (mime.indexOf('mpeg') !== -1 || mime.indexOf('mp3') !== -1) {
          ext = 'mp3'
        }
        formData.append('audio', this.recordedAudio, 'record.' + ext)
        
        const response = await this.$http.post('/api/upload/audio', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        const audioUrl = response.data.data.url
        
        const result = await this.$store.dispatch('submitAnswer', {
          questionId: this.currentQuestion.id,
          answer: audioUrl
        })
        
        this.nextData = result.next
        
        Toast.clear()
        Toast.success('上传成功，等待评分')
        
        setTimeout(() => {
          this.nextQuestion()
        }, 1500)
      } catch (err) {
        Toast.clear()
        Toast.fail(err.message)
      }
    },
    goToVip() {
      this.$router.push('/vip')
    },
    startTimer() {
      const timerKey = 'quiz_timer_cat_' + (this.$store.state.currentCategory?.id || 0)
      const savedTime = localStorage.getItem(timerKey)
      if (savedTime) {
        this.totalTime = parseInt(savedTime, 10)
      } else {
        this.totalTime = 0
      }

      this.timer = setInterval(() => {
        this.totalTime++
        localStorage.setItem(timerKey, this.totalTime)
      }, 1000)
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    formatTotalTime(seconds) {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.quiz-container {
  min-height: 100vh;
  background: linear-gradient(180deg, #f5f6fa 0%, #eef1f5 100%);
  padding-bottom: 20px;
  position: relative;
  overflow: hidden;
}

.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.06), rgba(118, 75, 162, 0.06));
}

.bg-circle-1 {
  width: 500px;
  height: 500px;
  top: -200px;
  right: -150px;
}

.bg-circle-2 {
  width: 350px;
  height: 350px;
  bottom: 200px;
  left: -120px;
}

.bg-circle-3 {
  width: 200px;
  height: 200px;
  bottom: 100px;
  right: 50px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.04), rgba(118, 75, 162, 0.04));
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding-top: env(safe-area-inset-top);
  position: relative;
  z-index: 10;
}

.header-content {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  position: relative;
  z-index: 1;
}

.header-left {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.back-btn:active {
  transform: scale(0.95);
  background: rgba(255, 255, 255, 0.3);
}

.header-center {
  flex: 1;
  text-align: center;
  width: auto;
  z-index: 99;
}

.quiz-title {
  font-size: 18px;
  font-weight: 700;
  color: white;
}

.quiz-progress-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 4px;
}

.header-right {
  width: 80px;
  display: flex;
  justify-content: flex-end;
}

.timer-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  color: white;
  backdrop-filter: blur(10px);
}

.timer-text {
  font-size: 13px;
  font-weight: 600;
  font-family: 'SF Mono', 'Monaco', monospace;
}

.progress-bar-wrapper {
  height: 8px;
  background: rgba(255, 255, 255, 0.25);
  position: relative;
  overflow: visible;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #ffd700, #ffaa00);
  border-radius: 0 4px 4px 0;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.progress-glow {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  background: #ffd700;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 12px rgba(255, 215, 0, 0.6);
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.content {
  padding: 24px 16px;
  position: relative;
  z-index: 5;
}

.question-card {
  background: white;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.card-glow {
  position: absolute;
  top: -50%;
  right: -20%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.08) 0%, transparent 70%);
  border-radius: 50%;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
}

.question-header-right {
  flex-shrink: 0;
}

.question-type-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.type-1 {
  background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
  color: #1890ff;
}

.type-2 {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  color: #52c41a;
}

.type-3 {
  background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%);
  color: #fa8c16;
}

.question-number {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.number-bg {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 14px;
  font-weight: 700;
  border-radius: 10px;
}

.number-text {
  font-size: 14px;
  color: #999;
  font-weight: 500;
}

.read-button-wrapper {
  margin-bottom: 16px;
}

.read-title-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1.5px solid #d6dcff;
  border-radius: 24px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  color: #5b6ee8;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.read-title-btn:hover {
  border-color: #667eea;
}

.read-title-btn.active {
  border-color: #667eea;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

.read-title-btn:active {
  transform: scale(0.97);
}

.read-title-icon {
  font-size: 18px;
}

.question-title {
  font-size: 19px;
  color: #1a1a2e;
  line-height: 1.8;
  margin: 0 0 32px;
  font-weight: 600;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 24px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 18px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  transform: translateY(12px);
  animation: option-fade-in 0.45s ease-out forwards;
  position: relative;
}

.option-item:hover {
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.option-item.selected {
  background: linear-gradient(135deg, #f5f0ff 0%, #e8f0ff 100%);
  border-color: #667eea;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.2);
  transform: scale(1.01);
}

.option-item.correct {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  border-color: #52c41a;
  box-shadow: 0 6px 20px rgba(82, 196, 26, 0.2);
}

.option-item.wrong {
  background: linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%);
  border-color: #ff4d4f;
  box-shadow: 0 6px 20px rgba(255, 77, 79, 0.2);
}

.option-key {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #666;
  transition: all 0.3s ease;
}

.option-item.selected .option-key {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.option-item.correct .option-key {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  color: white;
}

.option-item.wrong .option-key {
  background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
  color: white;
}

.key-inner {
  position: relative;
  z-index: 1;
}

.option-text {
  flex: 1;
  font-size: 16px;
  color: #333;
  font-weight: 500;
  line-height: 1.6;
}

.option-item.correct .option-text,
.option-item.wrong .option-text {
  color: #1a1a2e;
}

.option-indicator {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  color: white;
  font-size: 16px;
  font-weight: 700;
  border-radius: 50%;
}

.option-indicator.wrong {
  background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
}

.option-key {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #595959;
  font-size: 15px;
  transition: all 0.3s;
}

.option-item.selected .option-key {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.option-item.correct .option-key {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  color: white;
}

.option-item.wrong .option-key {
  background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
  color: white;
}

.option-text {
  flex: 1;
  font-size: 15px;
  color: #1a1a2e;
  line-height: 1.6;
}

.audio-section {
  margin-top: 20px;
}

.audio-card {
  background: linear-gradient(135deg, #f5f0ff 0%, #e8e8ff 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
}

.audio-visualizer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  height: 40px;
  margin-bottom: 16px;
}

.audio-visualizer .bar {
  width: 4px;
  height: 12px;
  background: #667eea;
  border-radius: 2px;
  transition: height 0.1s ease;
}

.audio-visualizer.playing .bar {
  animation: audio-bar 0.5s ease-in-out infinite;
}

@keyframes audio-bar {
  0%, 100% { height: 12px; }
  50% { height: 32px; }
}

.audio-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.audio-btn {
  min-width: 100px;
}

.audio-time {
  font-size: 14px;
  color: #595959;
  font-weight: 500;
}

.record-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 2px dashed #d9d9d9;
  margin-bottom: 16px;
  transition: all 0.3s;
}

.record-card.recording {
  border-color: #ff4d4f;
  background: linear-gradient(135deg, #fff1f0 0%, #fff5f5 100%);
}

.record-indicator {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 16px;
}

.record-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
  animation: record-pulse 1s ease-in-out infinite;
}

.record-dot:nth-child(2) { animation-delay: 0.2s; }
.record-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes record-pulse {
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}

.record-btn {
  width: 100%;
}

.record-time {
  text-align: center;
  margin-top: 12px;
  font-size: 24px;
  font-weight: 700;
  color: #ff4d4f;
}

.play-recorded-card {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  border-radius: 16px;
  padding: 24px;
  border: 2px solid #52c41a;
}

.recorded-audio-visualizer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  height: 32px;
  margin-bottom: 16px;
}

.recorded-audio-visualizer .bar {
  width: 4px;
  height: 10px;
  background: #52c41a;
  border-radius: 2px;
}

.recorded-audio-visualizer.playing .bar {
  animation: audio-bar 0.4s ease-in-out infinite;
}

.recorded-audio-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.result-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 28px;
  padding: 48px 32px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  opacity: 0;
  transform: scale(0.85);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 340px;
}

.modal-glow {
  position: absolute;
  top: -30%;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(82, 196, 26, 0.1) 0%, transparent 70%);
}

.modal-content.correct {
  border: 3px solid #52c41a;
  background: linear-gradient(180deg, #f6ffed 0%, #ffffff 100%);
}

.modal-content.correct .modal-glow {
  background: radial-gradient(circle, rgba(82, 196, 26, 0.15) 0%, transparent 70%);
}

.modal-content.wrong {
  border: 3px solid #ff4d4f;
  background: linear-gradient(180deg, #fff1f0 0%, #ffffff 100%);
}

.modal-content.wrong .modal-glow {
  background: radial-gradient(circle, rgba(255, 77, 79, 0.15) 0%, transparent 70%);
}

.modal-icon {
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}

.icon-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border: 4px solid rgba(82, 196, 26, 0.2);
  border-radius: 50%;
  animation: ring-pulse 1.5s ease-out infinite;
}

.modal-content.wrong .icon-ring {
  border-color: rgba(255, 77, 79, 0.2);
}

@keyframes ring-pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.4);
    opacity: 0;
  }
}

.modal-content.correct .modal-icon .van-icon {
  color: #52c41a;
}

.modal-content.wrong .modal-icon .van-icon {
  color: #ff4d4f;
}

.modal-content h3 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
  position: relative;
  z-index: 1;
}

.modal-content.correct h3 {
  color: #52c41a;
}

.modal-content.wrong h3 {
  color: #ff4d4f;
}

.modal-tip {
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}

.modal-next-btn {
  margin-top: 16px;
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%) !important;
  box-shadow: 0 4px 16px rgba(82, 196, 26, 0.4);
}

.score-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.score-label {
  font-size: 15px;
  color: #8c8c8c;
  font-weight: 500;
}

.score-value {
  font-size: 36px;
  font-weight: 800;
  color: #fa8c16;
  text-shadow: 0 2px 8px rgba(250, 140, 22, 0.3);
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
}

.footer-safe-area {
  height: env(safe-area-inset-bottom);
}

.footer-content {
  padding: 20px 24px;
  padding-bottom: 20px;
  background: white;
  box-shadow: 0 -8px 24px rgba(102, 126, 234, 0.08);
  border-radius: 24px 24px 0 0;
}

.footer-content .van-button {
  min-height: 56px;
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.submit-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.next-btn {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
}

.upload-btn {
  background: linear-gradient(135deg, #fa8c16 0%, #d46b08 100%);
  box-shadow: 0 6px 20px rgba(250, 140, 22, 0.4);
}

.record-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px;
  color: #8c8c8c;
  font-size: 14px;
  background: #f5f5f5;
  border-radius: 16px;
}

.start-dialog-content {
  padding: 32px 24px;
  text-align: center;
}

.start-icon {
  font-size: 80px;
  margin-bottom: 24px;
  animation: bounce-slow 2s ease-in-out infinite;
}

.start-dialog-content h3 {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 24px;
}

.start-info {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-bottom: 24px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #595959;
}

.start-tip {
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.6;
}

.vip-dialog-content {
  text-align: center;
  padding: 24px;
}

.vip-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.vip-dialog-content p {
  font-size: 15px;
  color: #1a1a2e;
  margin: 8px 0;
}

.vip-btn {
  margin-top: 16px;
  background: linear-gradient(135deg, #fa8c16 0%, #d46b08 100%);
}

@keyframes animate-card-pop {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes animate-fade-in-up {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes animate-bounce-in {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes option-fade-in {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce-slow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-card-pop {
  animation: animate-card-pop 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.animate-fade-in-up {
  animation: animate-fade-in-up 0.4s ease-out forwards;
}

.animate-bounce-in {
  animation: animate-bounce-in 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.hidden-audio {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.bank-gate {
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%);
  padding-bottom: calc(88px + env(safe-area-inset-bottom));
  position: relative;
  overflow: hidden;
}

.bank-gate::before {
  content: '';
  position: absolute;
  top: -100px;
  right: -50px;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.05) 0%, transparent 70%);
  border-radius: 50%;
}

.bank-gate::after {
  content: '';
  position: absolute;
  bottom: 100px;
  left: -50px;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(118, 75, 162, 0.04) 0%, transparent 70%);
  border-radius: 50%;
}

.bank-gate-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding-top: env(safe-area-inset-top);
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: center;
}

.bank-gate-header .header-content {
  display: flex;
  align-items: center;
  padding: 16px 0px;
}

.bank-gate-header .header-center {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}

.bank-gate-body {
  padding: 0;
  position: relative;
  z-index: 5;
}
</style>